import { createContext, useContext, type JSX } from 'react'
import { faCaretUp, faCaretDown } from '@fortawesome/pro-solid-svg-icons'
import { mtc, TextColors, TypographyStyles } from '../../../utils/style-helpers'
import {
  tableCellStyles,
  tableHeaderCellDualIconWrapperStyles,
  tableHeaderCellIconStyles,
  tableHeaderCellStyles,
  tableHeaderCellWrapperStyles,
  tableFooterStyles,
  tableHeaderStyles,
  tableRowStyles,
} from './styles'
import { Icon } from '../icon'
import { Text } from '../text'

const TableContext = createContext<{ compact?: 'very' }>({})
const useTableContext = () => useContext(TableContext)

type TableHeaderProps = JSX.IntrinsicElements['thead'] & {
  sticky?: boolean
}

export const TableHeader = ({
  className,
  sticky,
  ...props
}: TableHeaderProps) => (
  <thead className={mtc(tableHeaderStyles({ sticky }), className)} {...props} />
)

export const TableBody = ({
  className,
  ...props
}: JSX.IntrinsicElements['tbody']) => <tbody className={className} {...props} />

export const TableFooter = ({
  className,
  ...props
}: JSX.IntrinsicElements['tfoot']) => (
  <tfoot className={mtc(tableFooterStyles(), className)} {...props} />
)

export const TableRow = ({
  className,
  compact: propCompact,
  ...props
}: JSX.IntrinsicElements['tr'] & { compact?: 'very' }) => {
  const { compact: ctxCompact } = useTableContext()
  const compact = propCompact ?? ctxCompact
  return (
    <tr className={mtc(tableRowStyles({ compact }), className)} {...props} />
  )
}

export enum TableHeaderSort {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

export const TableHeaderCell = ({
  className,
  children,
  sorted,
  sortable,
  compact: propCompact,
  ...props
}: JSX.IntrinsicElements['th'] & {
  sorted?: TableHeaderSort
  sortable?: boolean
  width?: string | number
  compact?: 'very'
}) => {
  const { compact: ctxCompact } = useTableContext()
  const compact = propCompact ?? ctxCompact
  return (
    <th
      className={mtc(tableHeaderCellStyles({ sortable, compact }), className)}
      {...props}
    >
      <div className={mtc(tableHeaderCellWrapperStyles())}>
        {typeof children === 'string' ? (
          <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
            {children}
          </Text>
        ) : (
          children
        )}
        {sorted && (
          <Icon
            icon={
              sorted === TableHeaderSort.ASCENDING ? faCaretUp : faCaretDown
            }
            backgroundClassName={mtc(tableHeaderCellIconStyles())}
            color="text-neutral-500"
            className="h-2 w-2"
          />
        )}
        {sortable && !sorted && (
          <div className={mtc(tableHeaderCellDualIconWrapperStyles())}>
            <Icon
              icon={faCaretUp}
              backgroundClassName={mtc(
                tableHeaderCellIconStyles({ hasTwoIcons: true })
              )}
              className="h-2 w-2"
              color="text-neutral-500"
            />
            <Icon
              icon={faCaretDown}
              backgroundClassName={mtc(
                tableHeaderCellIconStyles({ hasTwoIcons: true })
              )}
              className="h-2 w-2"
              color="text-neutral-500"
            />
          </div>
        )}
      </div>
    </th>
  )
}

export const TableCell = ({
  className,
  children,
  textAlign,
  compact: propCompact,
  ...props
}: JSX.IntrinsicElements['td'] & {
  textAlign?: 'center' | 'left' | 'right'
  compact?: 'very'
}) => {
  const { compact: ctxCompact } = useTableContext()
  const compact = propCompact ?? ctxCompact
  return (
    <td className={mtc(tableCellStyles({ compact }), className)} {...props}>
      {typeof children === 'string' ? (
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.EMPHASIS}
          textAlign={textAlign}
        >
          {children}
        </Text>
      ) : (
        children
      )}
    </td>
  )
}

export const Table = ({
  className,
  compact,
  ...props
}: JSX.IntrinsicElements['table'] & { compact?: 'very' }) => (
  <TableContext.Provider value={{ compact }}>
    <table className={mtc('w-full', className)} {...props} />
  </TableContext.Provider>
)

Table.Header = TableHeader
Table.Body = TableBody
Table.Footer = TableFooter
Table.Row = TableRow
Table.HeaderCell = TableHeaderCell
Table.Cell = TableCell
